import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { colors2, getVal, SkeletonLoader, UI_BODY_SM, UI_BODY_SEMIBOLD_SM, UI_CAPTION_DF } from "oolib";
import { getRoutePathToPublishedContent } from "@okf/ootils";
import { __GetContentTypeConfigNew } from "@/utils/getters/gettersV2";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useSignalColors } from "@/components/analysisModules/comps/SignalFilters/useSignalColors";
import { useGetTopicClusteringTree } from "@/utils/react-query-hooks/aiAnnotation";
import { SignalChips } from "@/components/analysisModules/TopicClusteringTreeView/TopicCard";
import { DatasetCircles } from "@/components/analysisModules/TopicClusteringTreeView/DatasetCircles";

/**
 * The Highlights (Annotation Explorer) listing as a table, the same shape as
 * the topic drilldown's observation table: Observation (document + dataset),
 * Signal, What they said (the highlighted quote), Speaker, Date. A row opens
 * the annotation on its published page, as the card did. Drop-in alternative
 * to the masonry card grid; the parent keeps its own infinite-scroll sentinel.
 *
 * Sorting is the backend's: the table never reorders `data`. A sortable
 * column names the index field it sorts on (`sortField`), and a header click
 * hands `{ [sortField]: 1 | -1 }` to `setActiveSort` — the same `activeSort`
 * the annotations query already takes — so the order holds across pages.
 * Columns derived in the browser (priority, topic, subtopic) or on free-text
 * fields (explanation, signal) have no sortField and are not clickable.
 */
const Panel = styled.div`
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
  border-radius: 10px;
  overflow: hidden;
`;
const Scroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%;
  min-width: 2100px;
  border-collapse: collapse;
`;
const Th = styled.th`
  text-align: ${({ $num }) => ($num ? "right" : "left")};
  padding: 10px 22px;
  white-space: nowrap;
  border-bottom: 1px solid ${colors2.grey10};
  cursor: ${({ $sortable }) => ($sortable ? "pointer" : "default")};
  user-select: none;
`;
const HeadLabel = styled(UI_CAPTION_DF)`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  white-space: nowrap;
  color: ${colors2.grey60};
`;
const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.12s ease;
  &:not(:last-child) td {
    border-bottom: 1px solid ${colors2.grey5};
  }
  &:hover {
    background: ${colors2.grey2};
  }
`;
const Td = styled.td`
  padding: 12px 22px;
  vertical-align: top;
  text-align: ${({ $num }) => ($num ? "right" : "left")};
`;
// The speaker's name as a link to their detail page. A speaker's id is their
// actorName (the Customers table links the same way), so the link is built
// straight off the name.
// A global rule (sass/DEPRECATED_global.scss) sets `a { text-decoration: none
// !important; color: greyColor100 }`, so the link appearance has to be forced
// with doubled specificity (&&) and !important to show at all.
const SpeakerLink = styled(Link)`
  && {
    color: ${colors2.primary};
    text-decoration: underline !important;
    text-underline-offset: 2px;
  }
  white-space: nowrap;
  cursor: pointer;
  &&:hover {
    opacity: 0.8;
  }
`;
// The Dataset, Document and Topic / Subtopic cells are real anchors so the
// click opens their destination in a new tab: oolib's UI_BODY_SM drops
// arbitrary props (it forwards only style / className / color / children), so
// an onClick never reaches the DOM — a real <a target="_blank"> navigates
// natively instead. They read as links; the global `a { text-decoration: none
// !important; color: greyColor100 }` rule is beaten with doubled specificity
// (&&) + !important, the same way SpeakerLink does it. Font matches UI_BODY_SM
// (14 / 18, weight 400) so they line up with the text cells beside them.
const CellOpen = styled.a`
  && {
    color: ${colors2.primary};
    text-decoration: underline !important;
    text-underline-offset: 2px;
    font-family: "DM Sans", "Noto Sans Devanagari", "Noto Sans Gujarati",
      "Noto Sans Oriya", "Noto Sans Telugu", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    cursor: pointer;
  }
  &&:hover {
    opacity: 0.8;
  }
`;

const PERSONA_TAG_PREFIX = "personas_";

// An observation's priority is a proxy from its signal's category severity
// (the same severityWeight the Priorities view scores by) — there is no
// per-observation priority of its own. High / Medium / Low by the weight.
const PRIORITY_COLOR = { High: "#D4553A", Medium: "#D9A02A", Low: colors2.grey40 };
const priorityFromWeight = (w) =>
  w == null ? null : w >= 0.6 ? "High" : w >= 0.35 ? "Medium" : "Low";

// The annotation's signal: its first non-persona tag (persona tagIds are
// prefixed personas_).
const signalOf = (anno) => {
  const tagsData = Object.values(anno?.annotations?.tags || {}).find((d) => d?.collectionId === "tags")?.data || [];
  return tagsData.find((t) => t?.tagId && !String(t.tagId).startsWith(PERSONA_TAG_PREFIX)) || null;
};

// "28 Oct".
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

// sortField: the annotations-index field the backend sorts this column on
// (keyword fields; see okf-be staticMappings/annotations.js). Absent = not
// sortable. defaultDir: the direction a first click lands on.
const COLS = [
  { key: "observation", label: "Observation", sortField: "annotations.observation.title" },
  { key: "explanation", label: "Explanation" },
  { key: "signal", label: "Signal" },
  { key: "priority", label: "Priority" },
  { key: "topic", label: "Topic" },
  { key: "subtopic", label: "Subtopic" },
  { key: "dataset", label: "Dataset", sortField: "meta.contentType" },
  { key: "document", label: "Document", sortField: "main.title" },
  { key: "source", label: "Source", sortField: "meta.contentType" },
  { key: "speaker", label: "Speaker", sortField: "annotations.actorName" },
  { key: "date", label: "Date", num: true, sortField: "updatedAt", defaultDir: -1 },
];

// The one { field: 1 | -1 } pair an activeSort carries, or nulls.
const sortPair = (activeSort) => {
  const [field, dir] = Object.entries(activeSort || {})[0] || [];
  return { field: field || null, dir: dir === 1 || dir === -1 ? dir : null };
};

export const TableAnnoExplorer = ({ data, dataFetchStatus, hideSpeaker = false, activeSort, setActiveSort }) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const SELF_MANAGED_BASE_CONFIGS = platformConfigs?.SELF_MANAGED_BASE_CONFIGS;
  // Signal → category colour, shared with the Topics surfaces so a signal
  // reads the same colour here as it does there.
  const { colorOfSignal, categoryOfSignal, theme } = useSignalColors();
  const severityOfCategory = (cat) =>
    cat ? theme.list.find((c) => c.value === cat)?.severityWeight ?? null : null;
  // Topic / subtopic per observation: match its _id against the clustering
  // tree's node annotationIds (same as the speaker page does).
  const { data: treeDoc } = useGetTopicClusteringTree({ enabled: true });
  const topicByAnnoId = useMemo(() => {
    const map = {};
    (treeDoc?.tree || []).forEach((topic) => {
      const topicNodeId = topic.nodeId != null ? String(topic.nodeId) : null;
      (topic.children || []).forEach((sub) => {
        (sub.annotationIds || []).forEach((id) => {
          map[String(id)] = { topic: topic.label, subtopic: sub.label, topicNodeId };
        });
      });
      (topic.unplacedAnnotationIds || []).forEach((id) => {
        if (!map[String(id)]) map[String(id)] = { topic: topic.label, subtopic: null, topicNodeId };
      });
    });
    return map;
  }, [treeDoc]);
  // Backend sort: a header click flips the direction on the same field, or
  // lands on the column's default direction for a new field.
  const { field: sortedField, dir: sortedDir } = sortPair(activeSort);
  const canSort = typeof setActiveSort === "function";
  const toggleSort = (col) => {
    if (!canSort || !col.sortField) return;
    const nextDir = col.sortField === sortedField && sortedDir ? -sortedDir : col.defaultDir || 1;
    setActiveSort({ [col.sortField]: nextDir });
  };
  // The Speaker column is dropped on a single-speaker page (it's the same
  // speaker on every row there).
  const cols = hideSpeaker ? COLS.filter((c) => c.key !== "speaker") : COLS;

  if (dataFetchStatus === "loading" && !Array.isArray(data)) {
    return <SkeletonLoader style={{ width: "100%", height: 320, borderRadius: 10 }} />;
  }
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Panel style={{ padding: 24 }}>
        <UI_BODY_SM style={{ color: colors2.grey60 }}>No annotations match your current filters.</UI_BODY_SM>
      </Panel>
    );
  }

  // contentType → display title, for the source-logo circles' hover labels.
  const titleByContentType = {};
  data.forEach((a) => {
    const ct = a.meta?.contentType;
    if (ct && !(ct in titleByContentType)) {
      titleByContentType[ct] = __GetContentTypeConfigNew(ct)?.general?.content?.title || ct;
    }
  });

  // Rows come already ordered by the backend; the table never re-sorts them
  // (a client-side sort would only reorder the pages loaded so far).
  const rows = data;

  // The published page for an observation, scrolled to the highlight itself
  // (scrollIntoViewElemId). Opened in a new tab — the document link and the
  // whole-row click both use it.
  const hrefForAnno = (anno) => {
    const contentType = anno.meta?.contentType || anno.meta?.kp_content_type;
    const contentId = anno.meta?.documentId;
    if (!contentType || !contentId) return null;
    return (
      getRoutePathToPublishedContent({
        contentType,
        contentId,
        SELF_MANAGED_BASE_CONFIGS,
        scrollIntoViewElemId: anno.annotations?.annoKey,
      }) || null
    );
  };
  const openAnno = (anno) => {
    const href = hrefForAnno(anno);
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  };

  // The Topics listing, deep-linked to this observation's topic drilldown when
  // we know its nodeId (/topics?topic=<id>). There is no subtopic-level deep
  // link, so a subtopic opens its parent topic.
  const topicHref = (topicNodeId) =>
    topicNodeId ? `/topics?topic=${encodeURIComponent(topicNodeId)}` : "/topics";

  return (
    <Panel>
      <Scroll>
        <Table>
          <thead>
            <tr>
              {cols.map((c) => {
                const sortable = canSort && !!c.sortField;
                const sorted = sortable && c.sortField === sortedField;
                return (
                  <Th key={c.key} $num={c.num} $sortable={sortable} onClick={sortable ? () => toggleSort(c) : undefined}>
                    <HeadLabel>
                      {c.label}
                      {sorted ? (sortedDir === 1 ? " ↑" : " ↓") : ""}
                    </HeadLabel>
                  </Th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((anno) => {
              const tplConfig = __GetContentTypeConfigNew(getVal(anno, "meta.contentType"));
              const dataset = tplConfig?.general?.content?.title || anno.meta?.contentType;
              const docTitle = anno.main?.title;
              const contentType = anno.meta?.contentType;
              const obsTitle = anno.annotations?.observation?.title;
              const gist = anno.annotations?.observation?.desc;
              const signal = signalOf(anno);
              const priority = priorityFromWeight(severityOfCategory(signal ? categoryOfSignal(signal._id) : null));
              const tp = topicByAnnoId[String(anno._id)];
              const speaker = anno.annotations?.actorName;
              return (
                <Tr
                  key={anno._id + anno.annotations?.annoKey}
                  onClick={(e) => {
                    // A click on one of the cell links navigates on its own —
                    // don't also open the document.
                    if (e.target.closest("a")) return;
                    openAnno(anno);
                  }}
                >
                  <Td style={{ width: 480, maxWidth: 480 }}>
                    {obsTitle ? (
                      <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{obsTitle}</UI_BODY_SEMIBOLD_SM>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td style={{ width: 480, maxWidth: 480 }}>
                    {gist ? (
                      <UI_BODY_SM style={{ color: colors2.grey60 }}>{gist}</UI_BODY_SM>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td>
                    {signal?.display ? (
                      <SignalChips
                        segments={[{
                          key: String(signal._id),
                          title: signal.display,
                          color: colorOfSignal(signal._id) || colors2.grey40,
                          value: 1,
                        }]}
                      />
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td>
                    {priority ? (
                      <SignalChips
                        segments={[{ key: "priority", title: priority, color: PRIORITY_COLOR[priority], value: 1 }]}
                      />
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td style={{ width: 200, maxWidth: 200 }}>
                    {tp?.topic ? (
                      <CellOpen href={topicHref(tp.topicNodeId)} target="_blank" rel="noopener noreferrer">
                        {tp.topic}
                      </CellOpen>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td style={{ width: 200, maxWidth: 200 }}>
                    {tp?.subtopic ? (
                      <CellOpen href={topicHref(tp.topicNodeId)} target="_blank" rel="noopener noreferrer">
                        {tp.subtopic}
                      </CellOpen>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td style={{ width: 220, maxWidth: 220 }}>
                    {dataset && contentType ? (
                      <CellOpen href={`/datasets/${encodeURIComponent(contentType)}`} target="_blank" rel="noopener noreferrer">
                        {dataset}
                      </CellOpen>
                    ) : dataset ? (
                      <UI_BODY_SM style={{ color: colors2.grey80 }}>{dataset}</UI_BODY_SM>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td style={{ width: 340, maxWidth: 340 }}>
                    {docTitle && hrefForAnno(anno) ? (
                      <CellOpen href={hrefForAnno(anno)} target="_blank" rel="noopener noreferrer">
                        {docTitle}
                      </CellOpen>
                    ) : docTitle ? (
                      <UI_BODY_SM style={{ color: colors2.grey80 }}>{docTitle}</UI_BODY_SM>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  <Td>
                    {contentType ? (
                      <DatasetCircles datasets={[contentType]} titleByContentType={titleByContentType} />
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </Td>
                  {!hideSpeaker && (
                    <Td>
                      {speaker ? (
                        <SpeakerLink
                          to={`/customers/${encodeURIComponent(speaker)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <UI_BODY_SM as="span" style={{ color: "inherit", textDecoration: "inherit", whiteSpace: "nowrap" }}>
                            {speaker}
                          </UI_BODY_SM>
                        </SpeakerLink>
                      ) : (
                        <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                      )}
                    </Td>
                  )}
                  <Td $num>
                    <UI_BODY_SM style={{ color: colors2.grey60, whiteSpace: "nowrap" }}>
                      {fmtDate(anno.updatedAt)}
                    </UI_BODY_SM>
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </Scroll>
    </Panel>
  );
};
